import { useState } from "react";
import { FiX, FiSearch, FiSend } from "react-icons/fi";
import toast from "react-hot-toast";
import useChatStore from "../../store/chatStore";
import useAuthStore from "../../store/authStore";
import { useSocket } from "../../context/SocketContext";
import { getConversationName, getConversationAvatar, getInitials } from "../../utils/helpers";
import styles from "./ForwardModal.module.css";

const ForwardModal = ({ message, onClose }) => {
  const { user } = useAuthStore();
  const { conversations, forwardMessage } = useChatStore();
  const socketRef = useSocket();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set()); // selected conv IDs
  const [forwarding, setForwarding] = useState(false);

  const filtered = conversations.filter((c) => {
    const name = getConversationName(c, user._id);
    return name.toLowerCase().includes(search.toLowerCase());
  });

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleForward = async () => {
    if (selected.size === 0) return;
    setForwarding(true);
    try {
      for (const convId of selected) {
        const msg = await forwardMessage(message._id, convId);
        socketRef?.current?.emit("message:forward", { conversationId: convId, message: msg });
      }
      toast.success(`Forwarded to ${selected.size} chat${selected.size > 1 ? "s" : ""}`);
      onClose();
    } catch {
      toast.error("Failed to forward");
    } finally {
      setForwarding(false);
    }
  };

  // Selected conversation names for bottom bar
  const selectedNames = conversations
    .filter((c) => selected.has(c._id))
    .map((c) => getConversationName(c, user._id));

  return (
    <div className={styles.overlay}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className={styles.header}>
          <button className={styles.closeBtn} onClick={onClose}>
            <FiX size={20} />
          </button>
          <span className={styles.title}>Forward message to</span>
        </div>

        {/* Search bar */}
        <div className={styles.searchWrap}>
          <FiSearch size={16} className={styles.searchIcon} />
          <input
            className={styles.searchInput}
            placeholder="Search name or number"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>

        {/* Recent chats label */}
        <div className={styles.sectionLabel}>Recent chats</div>

        {/* List */}
        <div className={styles.list}>
          {filtered.map((conv) => {
            const name = getConversationName(conv, user._id);
            const avatar = getConversationAvatar(conv, user._id);
            const isSelected = selected.has(conv._id);
            const isGroup = conv.isGroup;
            const subtitle = isGroup
              ? (conv.participants?.map((p) => p.name || "").filter(Boolean).join(", ") || "Group")
              : "";

            return (
              <div
                key={conv._id}
                className={`${styles.item} ${isSelected ? styles.itemSelected : ""}`}
                onClick={() => toggle(conv._id)}
              >
                {/* Checkbox */}
                <div className={`${styles.checkbox} ${isSelected ? styles.checkboxChecked : ""}`}>
                  {isSelected && (
                    <svg viewBox="0 0 12 10" width="12" height="10" fill="none">
                      <path d="M1 5l3.5 3.5L11 1" stroke="white" strokeWidth="2"
                        strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>

                {/* Avatar */}
                <div className="avatar" style={{ width: 46, height: 46, fontSize: 16, flexShrink: 0 }}>
                  {avatar ? <img src={avatar} alt={name} /> : getInitials(name)}
                </div>

                {/* Info */}
                <div className={styles.info}>
                  <span className={styles.name}>{name}</span>
                  {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
                </div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <p className={styles.empty}>No conversations found</p>
          )}
        </div>

        {/* Bottom bar — shown when something is selected */}
        {selected.size > 0 && (
          <div className={styles.bottomBar}>
            <span className={styles.selectedNames}>
              {selectedNames.slice(0, 2).join(", ")}
              {selectedNames.length > 2 && ` +${selectedNames.length - 2} more`}
            </span>
            <button
              className={styles.sendBtn}
              onClick={handleForward}
              disabled={forwarding}
              title="Forward"
            >
              <FiSend size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForwardModal;
